import { useMemo } from 'react';
import {
  Box, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { C, cardStatic } from '../../theme/adminTokens';

const COLUMN_COUNT = 6; // Entity, Space, Extra Share, Base Share CCU, Extra CCU, Total
const UNGROUPED_LABEL = 'Other'; // group header for a space with no entity match

// One step darker than the shared C.border token (#e2e8f0) — scoped to this table only,
// since the ask was to darken these specific divider lines, not the app-wide border color.
const ROW_BORDER = '#9ea4ac';
const ROW_BORDER_OUTER = '#617c9e';

const columnRuleSx = { borderRight: `1px solid ${ROW_BORDER}` };
const lastColumnSx = { borderRight: 'none' };

// Subtler than MUI's default row-hover (action.hover, ~4% black) and tinted navy to match
// the table's palette instead of a flat gray wash.
const rowHoverSx = { '&:hover': { bgcolor: 'rgba(7, 33, 70, 0.025)' } };

// Matches the header style already used in EventsTable.jsx — solid navy bar, white text —
// so the divider between header and body reads as a real visual break, not just a hairline.
const HEADER_BG = '#0a326d';
const headerColumnRuleSx = { borderRight: '1px solid rgba(255,255,255,0.18)' };
const headerLastColumnSx = { borderRight: 'none' };

const headerCellSx = {
  fontWeight: 700,
  fontSize: 11.5,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#ffffff',
  bgcolor: HEADER_BG,
  py: 0.75,
  ...headerColumnRuleSx,
};

const bodyCellSx = { py: 0.6, borderBottom: `1px solid ${ROW_BORDER}`, ...columnRuleSx };

// Base/Extra CCU read as too light at the default body weight (400) — bumped to 600 so the
// figures read as normal weight rather than thin, one step under Total's 800.
const numericCellSx = { ...bodyCellSx, fontWeight: 600 };

// Entity is its own column, merged (rowSpan) down the height of that entity's space rows via
// a distinct tint so it still reads as a label, not another data row — only the first row of
// each group renders this cell at all; the merge covers the rest.
const entityColumnCellSx = {
  ...bodyCellSx,
  py: 0.75,
  bgcolor: '#eef2f7',
  fontWeight: 700,
  fontSize: 12,
  color: C.navy,
  letterSpacing: 0.3,
  verticalAlign: 'middle',
};

// Total's text color distinguishes extra-share rows from base-only ones.
const EXTRA_SHARE_TOTAL_COLOR = '#0891B2';

// Label-over-value readout for the three live allocation figures shown beside "Spaces".
const SummaryStat = ({ label, value }) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography
      variant="caption"
      sx={{ display: 'block', color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, fontSize: 10.5 }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.navy }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Combined picker + live preview, one table. Replaces the earlier split design (a separate
 * dropdown picker plus a read-only preview table) — the same space is now identified,
 * decided on (the Extra Share switch), and previewed (Base/Extra/Total) in one row instead
 * of two disconnected places. Rows are grouped by a merged (rowSpan) Entity column cell;
 * toggling a switch reports the updated selection up immediately, and Base/Extra/Total
 * recompute from the in-progress (unsaved) settings + selection on every render.
 *
 * @param {object} props
 * @param {{spaceId: number, spaceName: string}[]} props.spaces full non-deleted space list
 * @param {number[]} props.selectedIds currently-checked extra-share space ids
 * @param {(next: number[]) => void} props.onChange reports the full selected id list up
 * @param {{baseShareCcu: number, extraShareCcu: number, allocatedTotalCcu: number, normalUnallocatedCcu: number, absoluteUnallocatedCcu: number}} props.computed
 * @param {Map<number, string>} [props.entityNameBySpaceId]
 * @param {boolean} [props.disabled]
 */
const CcuSpaceTable = ({
  spaces = [],
  selectedIds = [],
  onChange,
  computed,
  entityNameBySpaceId,
  disabled = false,
}) => {
  const selectedSet = useMemo(() => new Set(selectedIds.map(Number)), [selectedIds]);
  const baseShareCcu = computed?.baseShareCcu ?? 0;
  const extraShareCcu = computed?.extraShareCcu ?? 0;

  // Sorted by entity first, then space — guarantees each entity's rows are contiguous, so the
  // rowSpan Entity cell placed on the first row of each group unambiguously covers the rest.
  const rows = useMemo(() => {
    return spaces
      .map((space) => {
        const isExtraShare = selectedSet.has(space.spaceId);
        const rowExtraCcu = isExtraShare ? extraShareCcu : 0;
        return {
          spaceId: space.spaceId,
          spaceName: space.spaceName,
          entityName: entityNameBySpaceId?.get(space.spaceId) ?? UNGROUPED_LABEL,
          isExtraShare,
          baseShareCcu,
          extraCcu: rowExtraCcu,
          total: baseShareCcu + rowExtraCcu,
        };
      })
      .sort((a, b) => {
        if (a.entityName !== b.entityName) {
          if (a.entityName === UNGROUPED_LABEL) return 1;
          if (b.entityName === UNGROUPED_LABEL) return -1;
          return a.entityName.localeCompare(b.entityName);
        }
        return a.spaceName.localeCompare(b.spaceName);
      });
  }, [spaces, selectedSet, baseShareCcu, extraShareCcu, entityNameBySpaceId]);

  // Row count per entity — since rows are sorted with each entity contiguous, a simple
  // frequency count doubles as each group's size, i.e. the rowSpan for its Entity cell.
  const entityGroupSizes = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => counts.set(row.entityName, (counts.get(row.entityName) ?? 0) + 1));
    return counts;
  }, [rows]);

  const toggleSpace = (spaceId) => {
    const next = selectedSet.has(spaceId)
      ? selectedIds.map(Number).filter((id) => id !== spaceId)
      : [...selectedIds.map(Number), spaceId];
    onChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.navy, mb: 1.5 }}>
            Spaces
          </Typography>
          <Typography variant="body2" sx={{ color: C.muted }}>
            Turn on Extra Share for a space to give it a cut of the surplus CCU pool on top of its base share.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <SummaryStat label="Total Allocated" value={computed?.allocatedTotalCcu ?? 0} />
          <SummaryStat label="Unallocated (Normal)" value={computed?.normalUnallocatedCcu ?? 0} />
          <SummaryStat label="Unallocated (Absolute)" value={computed?.absoluteUnallocatedCcu ?? 0} />
        </Box>
      </Box>
      {/* overflow:hidden here (strict, not 'auto') is what makes the rounded corners actually
          clip the header's navy background — mixing border-radius with overflowX:'auto' on
          the same element lets the header's square corner poke past the radius in most
          browsers. Horizontal scrolling is delegated to the inner TableContainer instead. */}
      <Box sx={{ ...cardStatic, overflow: 'hidden', border: `1px solid ${ROW_BORDER_OUTER}` }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={headerCellSx}>Entity</TableCell>
                <TableCell align="center" sx={headerCellSx}>Space</TableCell>
                <TableCell align="center" sx={headerCellSx}>Extra Share</TableCell>
                <TableCell align="center" sx={headerCellSx}>Base Share CCU</TableCell>
                <TableCell align="center" sx={headerCellSx}>Extra CCU</TableCell>
                <TableCell align="center" sx={{ ...headerCellSx, ...headerLastColumnSx }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ py: 4, textAlign: 'center', color: C.muted }}>
                    No spaces available
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => {
                  const isFirstInGroup = idx === 0 || row.entityName !== rows[idx - 1].entityName;
                  return (
                    <TableRow key={row.spaceId} sx={rowHoverSx}>
                      {isFirstInGroup && (
                        <TableCell rowSpan={entityGroupSizes.get(row.entityName)} sx={entityColumnCellSx}>
                          {row.entityName}
                        </TableCell>
                      )}
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: C.navy }}>{row.spaceName}</TableCell>
                      <TableCell align="center" sx={bodyCellSx}>
                        <Switch
                          size="small"
                          checked={row.isExtraShare}
                          onChange={() => toggleSpace(row.spaceId)}
                          disabled={disabled}
                          slotProps={{ input: { 'aria-label': `Extra share for ${row.spaceName}` } }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={numericCellSx}>{row.baseShareCcu}</TableCell>
                      <TableCell align="center" sx={{ ...numericCellSx, color: row.isExtraShare ? C.navy : C.muted }}>
                        {row.extraCcu}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          ...bodyCellSx, ...lastColumnSx, fontWeight: 800,
                          color: row.isExtraShare ? EXTRA_SHARE_TOTAL_COLOR : C.navy,
                        }}
                      >
                        {row.total}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default CcuSpaceTable;
